import * as React from "react";
import { cn } from "@/lib/utils";

export interface CurrencyInputProps
  extends Omit<React.InputHTMLAttributes<HTMLInputElement>, "onChange" | "value"> {
  currency?: "BRL" | "USD" | "EUR" | "MXN";
  value: string;
  onChange: (value: string, valueCents: number) => void;
  error?: string | boolean;
  showCurrencySymbol?: boolean;
}

const currencyConfig: Record<string, { symbol: string; locale: string; decimalSep: string }> = {
  BRL: { symbol: "R$", locale: "pt-BR", decimalSep: "," },
  USD: { symbol: "$", locale: "en-US", decimalSep: "." },
  EUR: { symbol: "\u20AC", locale: "de-DE", decimalSep: "," },
  MXN: { symbol: "$", locale: "es-MX", decimalSep: "." },
};

/**
 * Formata um valor para o formato de moeda usando o locale adequado
 * @param value Valor em string (apenas números ou com formatação)
 * @param locale Locale para formatação (ex: "pt-BR", "en-US")
 * @returns Valor formatado
 */
function formatCurrencyValue(value: string, locale?: string): string {
  // Remove tudo exceto números
  const digits = value.replace(/\D/g, "");

  if (!digits) return "";

  // Converte para número (em centavos)
  const cents = parseInt(digits, 10);

  // Converte para unidade monetária
  const amount = cents / 100;

  // Formata com separador de milhar e decimal
  return amount.toLocaleString(locale || "pt-BR", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

/**
 * Extrai o valor em centavos de uma string formatada
 * @param value Valor formatado (ex: "1.234,56" ou "1,234.56")
 * @returns Valor em centavos (123456)
 */
function parseCentsFromFormatted(value: string): number {
  const digits = value.replace(/\D/g, "");
  return digits ? parseInt(digits, 10) : 0;
}

const CurrencyInput = React.forwardRef<HTMLInputElement, CurrencyInputProps>(
  ({
    className,
    currency = "BRL",
    value,
    onChange,
    error,
    showCurrencySymbol = true,
    ...props
  }, ref) => {
    const config = currencyConfig[currency] || currencyConfig.BRL;
    const symbol = config.symbol;

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const rawValue = e.target.value;
      const formatted = formatCurrencyValue(rawValue, config.locale);
      const cents = parseCentsFromFormatted(formatted);
      onChange(formatted, cents);
    };

    // Handle keyboard input for better UX
    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
      // Allow: backspace, delete, tab, escape, enter
      if (
        e.key === "Backspace" ||
        e.key === "Delete" ||
        e.key === "Tab" ||
        e.key === "Escape" ||
        e.key === "Enter"
      ) {
        return;
      }

      // Allow: Ctrl+A, Ctrl+C, Ctrl+V, Ctrl+X
      if (e.ctrlKey || e.metaKey) {
        return;
      }

      // Allow: home, end, left, right
      if (
        e.key === "Home" ||
        e.key === "End" ||
        e.key === "ArrowLeft" ||
        e.key === "ArrowRight"
      ) {
        return;
      }

      // Block non-numeric keys
      if (!/^\d$/.test(e.key)) {
        e.preventDefault();
      }
    };

    // Padding left baseado no tamanho do símbolo
    const paddingMap: Record<string, string> = {
      "R$": "pl-10",
      "$": "pl-8",
      "\u20AC": "pl-8",
    };
    const paddingClass = paddingMap[symbol] || "pl-8";

    // Placeholder baseado no separador decimal da moeda
    const placeholderMap: Record<string, string> = {
      ",": "0,00",
      ".": "0.00",
    };
    const placeholder = placeholderMap[config.decimalSep] || "0,00";

    return (
      <div className="w-full">
        <div className="relative">
          {showCurrencySymbol && (
            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground font-medium">
              {symbol}
            </span>
          )}
          <input
            type="text"
            inputMode="numeric"
            className={cn(
              "flex h-10 w-full rounded-lg border border-neutral-200 bg-white px-3 py-2 text-sm ring-offset-background transition-colors file:border-0 file:bg-transparent file:text-sm file:font-medium file:text-foreground placeholder:text-neutral-400 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-neutral-900 focus-visible:border-neutral-900 disabled:cursor-not-allowed disabled:opacity-50",
              showCurrencySymbol && paddingClass,
              error && "border-red-500 focus-visible:ring-red-500 focus-visible:border-red-500",
              className
            )}
            ref={ref}
            value={value}
            onChange={handleChange}
            onKeyDown={handleKeyDown}
            placeholder={placeholder}
            {...props}
          />
        </div>
        {typeof error === "string" && (
          <p className="mt-1.5 text-xs text-red-600">{error}</p>
        )}
      </div>
    );
  }
);

CurrencyInput.displayName = "CurrencyInput";

export { CurrencyInput, formatCurrencyValue, parseCentsFromFormatted };
